import React, { ReactElement } from 'react';
import { FormHorizontal } from '~/components/form/layout';
import {
  McmVideoAgent,
  McmVideoDuration,
  McmVideoOrientation,
  McmVideoType,
} from '~/tenants/mcm/performable/video/McmVideoConfig';
import ZodFieldCheckbox from '~/components/zod/ZodFieldCheckbox';
import { useZodFormFieldSingleValue } from '~/components/zod/ZodForm';
import { McmOrderContext } from '~/tenants/mcm/model/McmOrder';
import ZodFieldHidden from '~/components/zod/ZodFieldHidden';
import ZodFieldCards from '~/components/zod/ZodFieldCards';
import McmVideoJob from '~/tenants/mcm/performable/video/McmVideoJob';
import FormGroup from '~/components/form/FormGroup';
import { context_has_buyer } from '~lib/context';
import { TenantPackageAddOnRepeat } from '~/tenants/common/package';
import { TenantPerformableFormProps } from '~/tenants/common/form';
import ZodFieldInput from '~/components/zod/ZodFieldInput';
import { Row } from '~/components/layout/Sections';
import ZodFieldSelect from '~/components/zod/ZodFieldSelect';
import { format_money } from '~/components/Money';

export default function McmVideoForm({ context, persona }: TenantPerformableFormProps<McmOrderContext>): ReactElement {
  const type = useZodFormFieldSingleValue<McmVideoType>('type');

  const typeOptions = Object.values(McmVideoType).map((type) => {
    const calc = McmVideoJob.calculate(context, type);

    return {
      title: calc.name,
      value: type,
      cost: calc.revenue,
    };
  });

  return (
    <FormGroup>
      <FormHorizontal name="orientation" label="Orientation">
        <ZodFieldCards
          name="orientation"
          required
          options={[
            { title: 'Horizontal', value: McmVideoOrientation.HORIZONTAL },
            { title: 'Vertical', value: McmVideoOrientation.VERTICAL },
          ]}
        />
      </FormHorizontal>

      {context_has_buyer(context, 'crossroads') ? (
        <ZodFieldHidden name="type" value={McmVideoType.PREMIUM} />
      ) : (
        <FormHorizontal name="type" label="Video Type">
          <ZodFieldCards name="type" options={typeOptions} required />
        </FormHorizontal>
      )}

      <FormHorizontal name="agent" label="Agent Cameo">
        <ZodFieldCards
          name="agent"
          options={Object.values(McmVideoAgent).map((type) => {
            const calc = McmVideoJob.agent(context, type);
            return { title: calc.name, cost: calc.amount, value: type };
          })}
        />
      </FormHorizontal>

      {!context_has_buyer(context, 'crossroads') && type !== McmVideoType.HIGHLIGHT && (
        <FormHorizontal name="day_to_night" label="Day to Night">
          <ZodFieldCheckbox name="day_to_night" />
        </FormHorizontal>
      )}

      <TenantPackageAddOnRepeat
        name="extra"
        image="tenant/bre/cinematic/thumbnail.jpg"
        title="Extra Edits"
        persona={persona}
      >
        <Row nowrap>
          <ZodFieldSelect
            name="orientation"
            options={[
              { label: 'Horizontal', value: McmVideoOrientation.HORIZONTAL },
              { label: 'Vertical', value: McmVideoOrientation.VERTICAL },
            ]}
          />
          <ZodFieldSelect
            name="duration"
            options={[
              {
                label: '15 Seconds w/ Audio',
                value: McmVideoDuration.AUDIO15,
                hidden: !context_has_buyer(context, 'sothebys'),
              },
              {
                label: '15 Seconds w/o Audio',
                value: McmVideoDuration.NOAUDIO15,
                hidden: !context_has_buyer(context, 'sothebys'),
              },
              { label: '15 Seconds', value: McmVideoDuration.AUDIO15, hidden: context_has_buyer(context, 'sothebys') },
              { label: '30 Seconds', value: McmVideoDuration.AUDIO30 },
              { label: '60 Seconds', value: McmVideoDuration.AUDIO60 },
              { label: '90 Seconds', value: McmVideoDuration.AUDIO90 },
              { label: '3 Minutes', value: McmVideoDuration.AUDIO180 },
            ].map((option) => ({
              ...option,
              label: `${option.label}: ${format_money(McmVideoJob.duration(option.value))}`,
            }))}
          />
        </Row>
      </TenantPackageAddOnRepeat>
    </FormGroup>
  );
}
